/**
 * A function to create a log object for the 'loyaltyRewardCreateOrEditServer' event (evid: 1)
 *
 * Event description: Event being triggered whenever reward is created/updated.
 *
 * @param {loyaltyRewardCreateOrEditServerParams} params - The parameters for this event
 * @property {string} targetAppId - app id(s) that this reward can be used at.
 * @property {string} rewardId - reward_id
 * @property {number} revision - # of how many times it was changed. Initial = 1
 * @property {string} rewardType - reward type. Currently we have two types
 * @property {boolean} isDeleted - is deleted T/F
 * @property {string} rewardProperties - json with properties. Each reward type has different properties
 * @property {boolean} isEnabled - if reward is enabled or not
 * @property {string} rewardName - reward name
 * @property {number} pointsRequired - points required to use this reward
 * @return {LogObject<loyaltyRewardCreateOrEditServerParams>} LogObject<loyaltyRewardCreateOrEditServerParams>
 */
export function loyaltyRewardCreateOrEditServer(params) {
    return { evid: 1, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUserClickBm' event (evid: 100)
 *
 * Event description: User click at Loyalty app. Might be button, link, toast and etc.
 *
 * @param {loyaltyUserClickBmParams} params - The parameters for this event
 * @property {string} referralInfo - referral_info
 * @property {string} info - additional info
 * @property {string} origin - dashboard/editor
 * @property {string} value - name of the button, link, toggle (etc) that was clicked by user
 * @return {LogObject<loyaltyUserClickBmParams>} LogObject<loyaltyUserClickBmParams>
 */
export function loyaltyUserClickBm(params) {
    return { evid: 100, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltySetupWizardStartClick' event (evid: 102)
 *
 * Event description: Start click of Loyalty setup wizard
 *
 * @param {loyaltySetupWizardStartClickParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} biLocation - wizard_fs/wizard_mo/bm
 * @property {string} origin - dashboard/editor
 * @return {LogObject<loyaltySetupWizardStartClickParams>} LogObject<loyaltySetupWizardStartClickParams>
 */
export function loyaltySetupWizardStartClick(params) {
    return { evid: 102, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltySetupWizardScreenOpen' event (evid: 103)
 *
 * Event description: Fires when one of the Loyalty setup wizard screen opens
 *
 * @param {loyaltySetupWizardScreenOpenParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} biLocation - wizard_fs/wizard_mo/bm
 * @property {string} previousScreen - screen user came from
 * @property {string} origin - dashboard/editor
 * @property {string} screen - name of the screen that was opened
 * @return {LogObject<loyaltySetupWizardScreenOpenParams>} LogObject<loyaltySetupWizardScreenOpenParams>
 */
export function loyaltySetupWizardScreenOpen(params) {
    return { evid: 103, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltySetupWizardLaunchClick' event (evid: 104)
 *
 * Event description: fires when user click on "Launch Program"
 *
 * @param {loyaltySetupWizardLaunchClickParams} params - The parameters for this event
 * @property {string} pointsName - name of the points
 * @property {string} referralInfo - place where user came from
 * @property {boolean} discountPercentageToggle - discount percentage toggle t/f
 * @property {boolean} isTextChanged - text changed t/f
 * @property {string} biLocation - wizard_fs/wizard_mo/bm
 * @property {boolean} isIconChanged - icon changed t/f
 * @property {string} rceComponentsUsed - RCE components that were used
 * @property {string} programName - name of the program
 * @property {string} origin - dashboard/editor
 * @property {boolean} discountAmountToggle - discount amount toggle t/f
 * @property {boolean} isPremium - if the user has the required premium plan - true; otherwise false
 * @return {LogObject<loyaltySetupWizardLaunchClickParams>} LogObject<loyaltySetupWizardLaunchClickParams>
 */
export function loyaltySetupWizardLaunchClick(params) {
    return { evid: 104, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyBoOpen' event (evid: 105)
 *
 * Event description: fires when Loyalty BO opens
 *
 * @param {loyaltyBoOpenParams} params - The parameters for this event
 * @property {string} origin - dashboard/editor
 * @property {string} referralInfo - place where user came from
 * @return {LogObject<loyaltyBoOpenParams>} LogObject<loyaltyBoOpenParams>
 */
export function loyaltyBoOpen(params) {
    return { evid: 105, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyManageProgramOpen' event (evid: 106)
 *
 * Event description: fires when Loyalty Manage Program opens
 *
 * @param {loyaltyManageProgramOpenParams} params - The parameters for this event
 * @property {string} origin - dashboard/editor
 * @property {string} referralInfo - place where user came from
 * @return {LogObject<loyaltyManageProgramOpenParams>} LogObject<loyaltyManageProgramOpenParams>
 */
export function loyaltyManageProgramOpen(params) {
    return { evid: 106, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyProgramEditSaveClick' event (evid: 107)
 *
 * Event description: when User edits Loyalty program
 *
 * @param {loyaltyProgramEditSaveClickParams} params - The parameters for this event
 * @property {string} pointsName - name of the points
 * @property {string} referralInfo - place where user came from
 * @property {boolean} isTextChanged - text changed t/f
 * @property {boolean} isIconChanged - icon changed t/f
 * @property {string} rceComponentsUsed - list of RCE components used
 * @property {string} programName - name of the program
 * @property {string} origin - dashboard/editor
 * @property {string} formOrigin - Used only on WixApp. Program can be edited at two places
 * @return {LogObject<loyaltyProgramEditSaveClickParams>} LogObject<loyaltyProgramEditSaveClickParams>
 */
export function loyaltyProgramEditSaveClick(params) {
    return { evid: 107, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyRewardToggleClick' event (evid: 108)
 *
 * Event description: reward toggle turn on/off
 *
 * @param {loyaltyRewardToggleClickParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} biLocation - wizard_fs/wizard_mo/bm
 * @property {GUID} rewardId - reward_id from backend
 * @property {string} rewardType - type of reward
 * @property {string} origin - dashboard/editor
 * @property {string} screen - name of the field reward being updated: BO, get_rewards
 * @property {boolean} isEnabled - reward enabled/disabled t/f
 * @property {string} rewardName - displayed reward name
 * @property {number} toGet - discount you will get
 * @property {number} pointsRequired - points required to use this reward
 * @return {LogObject<loyaltyRewardToggleClickParams>} LogObject<loyaltyRewardToggleClickParams>
 */
export function loyaltyRewardToggleClick(params) {
    return { evid: 108, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyRewardEditOrSave' event (evid: 109)
 *
 * Event description: Loyalty reward edit or save
 *
 * @param {loyaltyRewardEditOrSaveParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} pointsRequiredMap - map of tier_ids and points required, basic tier id is always null
 * @property {string} biLocation - wizard_fs/wizard_mo/bm
 * @property {GUID} rewardId - reward_id from backend
 * @property {string} rewardType - type of reward
 * @property {string} origin - dashboard/editor
 * @property {string} screen - name of the field reward being updated: BO, get_rewards
 * @property {boolean} isEnabled - isEnabled
 * @property {string} rewardName - displayed reward name
 * @property {string} action - action that user did: open/save/edit
 * @property {number} toGet - amount that customer will get
 * @property {number} pointsRequired - points required to use this reward
 * @return {LogObject<loyaltyRewardEditOrSaveParams>} LogObject<loyaltyRewardEditOrSaveParams>
 */
export function loyaltyRewardEditOrSave(params) {
    return { evid: 109, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyContactView' event (evid: 110)
 *
 * Event description: when user views UoU
 *
 * @param {loyaltyContactViewParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} origin - dashboard/editor
 * @property {GUID} viewedContactId - uuid of contact that was viewed
 * @return {LogObject<loyaltyContactViewParams>} LogObject<loyaltyContactViewParams>
 */
export function loyaltyContactView(params) {
    return { evid: 110, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUouCheckoutLogin' event (evid: 1100)
 *
 * Event description: UoU login click from Loyalty checkout plugin
 *
 * @param {loyaltyUouCheckoutLoginParams} params - The parameters for this event
 * @return {LogObject<loyaltyUouCheckoutLoginParams>} LogObject<loyaltyUouCheckoutLoginParams>
 */
export function loyaltyUouCheckoutLogin(params) {
    return { evid: 1100, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUouCheckoutGetReward' event (evid: 1101)
 *
 * Event description: UoU Get Reward click from Loyalty checkout plugin
 *
 * @param {loyaltyUouCheckoutGetRewardParams} params - The parameters for this event
 * @property {number} points - Number of loyalty points to redeem
 * @return {LogObject<loyaltyUouCheckoutGetRewardParams>} LogObject<loyaltyUouCheckoutGetRewardParams>
 */
export function loyaltyUouCheckoutGetReward(params) {
    return { evid: 1101, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUouCheckoutClicks' event (evid: 1102)
 *
 * Event description: Radio button navigation log
 *
 * @param {loyaltyUouCheckoutClicksParams} params - The parameters for this event
 * @property {string} couponDropdown - value selected from dropdown
 * @property {string} click - clicked field - flexible/fixed
 * @return {LogObject<loyaltyUouCheckoutClicksParams>} LogObject<loyaltyUouCheckoutClicksParams>
 */
export function loyaltyUouCheckoutClicks(params) {
    return { evid: 1102, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUoUModalClickSrc136Evid1103' event (evid: 1103)
 *
 * Event description: sent when UoU interacts with Loyalty modals
 *
 * @param {loyaltyUoUModalClickSrc136Evid1103Params} params - The parameters for this event
 * @property {string} modalOrigin - where the modal was shown
 * @property {string} modalClick - what was clicked on the modal
 * @property {string} modalName - the name of the modal
 * @return {LogObject<loyaltyUoUModalClickSrc136Evid1103Params>} LogObject<loyaltyUoUModalClickSrc136Evid1103Params>
 */
export function loyaltyUoUModalClickSrc136Evid1103(params) {
    return { evid: 1103, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyPointsFlow' event (evid: 111)
 *
 * Event description: actions that user did until entered edit or give points screen
 *
 * @param {loyaltyPointsFlowParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} origin - dashboard/editor
 * @property {string} flow - there are two flows: edit (to edit points) & give (to give points)
 * @property {GUID} viewedContactId - uuid of contact that user did action
 * @property {string} action - usually button identifier that user clicked
 * @return {LogObject<loyaltyPointsFlowParams>} LogObject<loyaltyPointsFlowParams>
 */
export function loyaltyPointsFlow(params) {
    return { evid: 111, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyPointsGiveOrEdit' event (evid: 112)
 *
 * Event description: when User gives or edits UoU points
 *
 * @param {loyaltyPointsGiveOrEditParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {number} pointsAfter - points after edit/give
 * @property {number} pointsBefore - points before edit/give
 * @property {string} origin - dashboard/editor
 * @property {string} flow - there are two flows: edit (to edit points) & give (to give points)
 * @property {GUID} viewedContactId - uuid of contact that user did action
 * @return {LogObject<loyaltyPointsGiveOrEditParams>} LogObject<loyaltyPointsGiveOrEditParams>
 */
export function loyaltyPointsGiveOrEdit(params) {
    return { evid: 112, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyBoOpenFail' event (evid: 113)
 *
 * Event description: fires when Loyalty BO fails to open
 *
 * @param {loyaltyBoOpenFailParams} params - The parameters for this event
 * @property {string} origin - dashboard/editor
 * @property {string} referralInfo - place where user came from
 * @return {LogObject<loyaltyBoOpenFailParams>} LogObject<loyaltyBoOpenFailParams>
 */
export function loyaltyBoOpenFail(params) {
    return { evid: 113, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyMiscLoad' event (evid: 114)
 *
 * Event description: A load of miscellaneous loyalty components. Might be banner, toast, modal, etc.
 *
 * @param {loyaltyMiscLoadParams} params - The parameters for this event
 * @property {string} referralInfo - referralInfo
 * @property {string} origin - dashboard/editor
 * @property {string} value - name of loaded component
 * @return {LogObject<loyaltyMiscLoadParams>} LogObject<loyaltyMiscLoadParams>
 */
export function loyaltyMiscLoad(params) {
    return { evid: 114, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyEarnPointsToggleClick' event (evid: 115)
 *
 * Event description: Earn points toggle turn on/off. Mobile-only, as web events belong to the automation team.
 *
 * @param {loyaltyEarnPointsToggleClickParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} toggleName - Signup_reward/Spend_reward/Invoice_reward
 * @property {number} pointsEarned - Points_earned
 * @property {string} biLocation - wizard_fs/wizard_mo/bm/wixapp_wizard/wixapp_bm
 * @property {GUID} rewardId - reward_id from backend
 * @property {string} rewardType - type of reward
 * @property {string} origin - dashboard/editor
 * @property {string} screen - name of the field reward being updated: BO, get_rewards
 * @property {boolean} isEnabled - reward enabled/disabled t/f
 * @property {string} rewardName - displayed reward name
 * @return {LogObject<loyaltyEarnPointsToggleClickParams>} LogObject<loyaltyEarnPointsToggleClickParams>
 */
export function loyaltyEarnPointsToggleClick(params) {
    return { evid: 115, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyEarnPointsEditOrSave' event (evid: 116)
 *
 * Event description: Loyalty Earn Points - Edit or Save.
 *
 * @param {loyaltyEarnPointsEditOrSaveParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} pointsMap - points map [tier1_id, points1, tier2_id, points2 ] Base tier ID is always null
 * @property {string} biLocation - wizard_fs/wizard_mo/bm/wixapp_wizard/wixapp_bm
 * @property {GUID} rewardId - reward_id from backend
 * @property {string} rewardType - type of reward
 * @property {number} points - Amount of points that is rewarded in Base tier
 * @property {string} origin - dashboard/editor
 * @property {string} screen - name of the field reward being updated: BO, get_rewards
 * @property {boolean} isEnabled - isEnabled
 * @property {string} rewardName - displayed reward name
 * @property {string} action - action that user did: open/save
 * @property {string} additionalInfo - Any additional information for the earning rule
 * @return {LogObject<loyaltyEarnPointsEditOrSaveParams>} LogObject<loyaltyEarnPointsEditOrSaveParams>
 */
export function loyaltyEarnPointsEditOrSave(params) {
    return { evid: 116, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyToastClickGoToEditor' event (evid: 117)
 *
 * Event description: Loyalty toast click "Go to editor" to finish loyalty installation.
 *
 * @param {loyaltyToastClickGoToEditorParams} params - The parameters for this event
 * @property {string} origin - dashboard/editor
 * @property {string} referralInfo - place where user came from i.e. Loyalty BM/Restaurants BM, etc
 * @return {LogObject<loyaltyToastClickGoToEditorParams>} LogObject<loyaltyToastClickGoToEditorParams>
 */
export function loyaltyToastClickGoToEditor(params) {
    return { evid: 117, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyTiersEditOrSave' event (evid: 118)
 *
 * Event description: Sent when User clicks to Edit or Save a Tier
 *
 * @param {loyaltyTiersEditOrSaveParams} params - The parameters for this event
 * @property {string} tierName - displayed tier name
 * @property {string} biLocation - wizard_fs/wizard_mo/bm/wixapp_wizard/wixapp_bm
 * @property {string} origin - dashboard/editor
 * @property {number} pointsTreshold - number of points needed to qualify
 * @property {string} action - action that user did: open/save
 * @property {GUID} loyaltyTierId - tier_id from backend
 * @return {LogObject<loyaltyTiersEditOrSaveParams>} LogObject<loyaltyTiersEditOrSaveParams>
 */
export function loyaltyTiersEditOrSave(params) {
    return { evid: 118, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyEmailAutomationAction' event (evid: 119)
 *
 * Event description: Sent when User toggles or clicks to edit email automation rules
 *
 * @param {loyaltyEmailAutomationActionParams} params - The parameters for this event
 * @property {string} value - on/off/edit
 * @property {string} automationType - name/type of the automation e.g. reward earned
 * @return {LogObject<loyaltyEmailAutomationActionParams>} LogObject<loyaltyEmailAutomationActionParams>
 */
export function loyaltyEmailAutomationAction(params) {
    return { evid: 119, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyTiersSettings' event (evid: 120)
 *
 * Event description: Managing of Loyalty settings events (expiration, etc)
 *
 * @param {loyaltyTiersSettingsParams} params - The parameters for this event
 * @property {string} expirationTime - 1year, 2 years, etc
 * @property {string} value - edit/save
 * @return {LogObject<loyaltyTiersSettingsParams>} LogObject<loyaltyTiersSettingsParams>
 */
export function loyaltyTiersSettings(params) {
    return { evid: 120, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyTiersStatusChange' event (evid: 121)
 *
 * Event description: sent when tiers are being setup/enabled/disabled
 *
 * @param {loyaltyTiersStatusChangeParams} params - The parameters for this event
 * @property {string} value - setup-intent/setup-confirm/disable-toggle/disable-confirm, etc
 * @return {LogObject<loyaltyTiersStatusChangeParams>} LogObject<loyaltyTiersStatusChangeParams>
 */
export function loyaltyTiersStatusChange(params) {
    return { evid: 121, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyAddCheckoutWidget' event (evid: 122)
 *
 * Event description: Sent when user shows intent to add checkout widget
 *
 * @param {loyaltyAddCheckoutWidgetParams} params - The parameters for this event
 * @property {string} origin - program_live_modal, program_details, preview_modal
 * @return {LogObject<loyaltyAddCheckoutWidgetParams>} LogObject<loyaltyAddCheckoutWidgetParams>
 */
export function loyaltyAddCheckoutWidget(params) {
    return { evid: 122, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyConfirmAddCheckoutWidget' event (evid: 123)
 *
 * Event description: Sent when user confirms/cancels adding checkout widget
 *
 * @param {loyaltyConfirmAddCheckoutWidgetParams} params - The parameters for this event
 * @property {string} value - add, cancel
 * @return {LogObject<loyaltyConfirmAddCheckoutWidgetParams>} LogObject<loyaltyConfirmAddCheckoutWidgetParams>
 */
export function loyaltyConfirmAddCheckoutWidget(params) {
    return { evid: 123, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyLearnMoreCheckoutWidget' event (evid: 124)
 *
 * Event description: Sent when user clicks Learn more when adding checkout widget
 *
 * @param {loyaltyLearnMoreCheckoutWidgetParams} params - The parameters for this event
 * @property {string} origin - preview_modal, program_live_modal
 * @return {LogObject<loyaltyLearnMoreCheckoutWidgetParams>} LogObject<loyaltyLearnMoreCheckoutWidgetParams>
 */
export function loyaltyLearnMoreCheckoutWidget(params) {
    return { evid: 124, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'modalShown' event (evid: 125)
 *
 * Event description: Generic event for different modals
 *
 * @param {modalShownParams} params - The parameters for this event
 * @property {string} origin - origin
 * @property {string} modalName - modal name
 * @return {LogObject<modalShownParams>} LogObject<modalShownParams>
 */
export function modalShown(params) {
    return { evid: 125, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'modalAction' event (evid: 126)
 *
 * Event description: Generic event for different modals
 *
 * @param {modalActionParams} params - The parameters for this event
 * @property {string} dataJson - If the modal has selections for the user, those selections should be stored here
 * @property {string} origin - origin
 * @property {string} modalName - modal name
 * @property {string} action - Action on modal (close/save/discard and etc)
 * @return {LogObject<modalActionParams>} LogObject<modalActionParams>
 */
export function modalAction(params) {
    return { evid: 126, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyMiscClick' event (evid: 127)
 *
 * Event description: User clicks one of non-essential buttons
 *
 * @param {loyaltyMiscClickParams} params - The parameters for this event
 * @property {string} value - button name (upgrade, upgrade now, go to editor, etc)
 * @return {LogObject<loyaltyMiscClickParams>} LogObject<loyaltyMiscClickParams>
 */
export function loyaltyMiscClick(params) {
    return { evid: 127, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportPrepare' event (evid: 129)
 *
 * Event description: user clicks to learn more in the prepare stage of importing
 *
 * @param {loyaltyImportPrepareParams} params - The parameters for this event
 * @property {boolean} clickLink - user clicked 'Learn more'
 * @return {LogObject<loyaltyImportPrepareParams>} LogObject<loyaltyImportPrepareParams>
 */
export function loyaltyImportPrepare(params) {
    return { evid: 129, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportUploadFile' event (evid: 130)
 *
 * Event description: actions/outcomes in the upload stage of importing
 *
 * @param {loyaltyImportUploadFileParams} params - The parameters for this event
 * @property {number} rowsFound - number of rows found in the file
 * @return {LogObject<loyaltyImportUploadFileParams>} LogObject<loyaltyImportUploadFileParams>
 */
export function loyaltyImportUploadFile(params) {
    return { evid: 130, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportMatchInitiated' event (evid: 131)
 *
 * Event description: actions/outcomes of the matching stage in the import flow
 *
 * @param {loyaltyImportMatchInitiatedParams} params - The parameters for this event
 * @property {boolean} matchInitiated - column data matching was initiated by user
 * @return {LogObject<loyaltyImportMatchInitiatedParams>} LogObject<loyaltyImportMatchInitiatedParams>
 */
export function loyaltyImportMatchInitiated(params) {
    return { evid: 131, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportMatchingError' event (evid: 132)
 *
 * Event description: Triggered when there is a matching error
 *
 * @param {loyaltyImportMatchingErrorParams} params - The parameters for this event
 * @property {string} matchingError - why matching was unsuccessful
 * @return {LogObject<loyaltyImportMatchingErrorParams>} LogObject<loyaltyImportMatchingErrorParams>
 */
export function loyaltyImportMatchingError(params) {
    return { evid: 132, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportImportSuccess' event (evid: 133)
 *
 * Event description: Triggers when user 100% imported their customers in the last import flow stage
 *
 * @param {loyaltyImportImportSuccessParams} params - The parameters for this event
 * @property {number} numberImported - how many customers were successfully imported
 * @return {LogObject<loyaltyImportImportSuccessParams>} LogObject<loyaltyImportImportSuccessParams>
 */
export function loyaltyImportImportSuccess(params) {
    return { evid: 133, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportImportPartialSuccess' event (evid: 134)
 *
 * Event description: outcomes/user actions in the last stage of the import flow under partial success
 *
 * @param {loyaltyImportImportPartialSuccessParams} params - The parameters for this event
 * @property {number} numberNotImported - number of not imported customers
 * @property {number} numberImported - number of imported customers
 * @return {LogObject<loyaltyImportImportPartialSuccessParams>} LogObject<loyaltyImportImportPartialSuccessParams>
 */
export function loyaltyImportImportPartialSuccess(params) {
    return { evid: 134, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportImportFailure' event (evid: 135)
 *
 * Event description: Triggered when upload has 100% errors (no customers imported) in the last stage of the import flow
 *
 * @param {loyaltyImportImportFailureParams} params - The parameters for this event
 * @property {number} numberNotImported - number of customers not imported
 * @return {LogObject<loyaltyImportImportFailureParams>} LogObject<loyaltyImportImportFailureParams>
 */
export function loyaltyImportImportFailure(params) {
    return { evid: 135, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportUploadFileError' event (evid: 136)
 *
 * Event description: Errors in the upload stage of importing
 *
 * @param {loyaltyImportUploadFileErrorParams} params - The parameters for this event
 * @property {string} fileUploadError - why file was not uploaded successfully
 * @return {LogObject<loyaltyImportUploadFileErrorParams>} LogObject<loyaltyImportUploadFileErrorParams>
 */
export function loyaltyImportUploadFileError(params) {
    return { evid: 136, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportErrorFileDownload' event (evid: 137)
 *
 * Event description: User downloads file containing errors. File is generated after partial or failure outcomes in the import flow end
 *
 * @param {loyaltyImportErrorFileDownloadParams} params - The parameters for this event
 * @return {LogObject<loyaltyImportErrorFileDownloadParams>} LogObject<loyaltyImportErrorFileDownloadParams>
 */
export function loyaltyImportErrorFileDownload(params) {
    return { evid: 137, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyImportTryAgain' event (evid: 138)
 *
 * Event description: After Import failure in the last stage of the import flow user clicks to try again
 *
 * @param {loyaltyImportTryAgainParams} params - The parameters for this event
 * @return {LogObject<loyaltyImportTryAgainParams>} LogObject<loyaltyImportTryAgainParams>
 */
export function loyaltyImportTryAgain(params) {
    return { evid: 138, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pointsExpirationStatusChange' event (evid: 139)
 *
 * Event description: Sent when points expiration is being setup/enabled/disabled
 *
 * @param {pointsExpirationStatusChangeParams} params - The parameters for this event
 * @property {string} value - setup-intent/setup-confirm/disable-toggle/disable-confirm, etc
 * @return {LogObject<pointsExpirationStatusChangeParams>} LogObject<pointsExpirationStatusChangeParams>
 */
export function pointsExpirationStatusChange(params) {
    return { evid: 139, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pointsExpirationSettings' event (evid: 140)
 *
 * Event description: Sent when user specifies the conditions of point expiration
 *
 * @param {pointsExpirationSettingsParams} params - The parameters for this event
 * @property {boolean} sendReminder - true/false if points reminder email is enabled
 * @property {number} amountPoints - how many points expire, percentage
 * @property {string} biLocation - setup/edit
 * @property {string} inactivity - after how many months of inactivity points expire
 * @return {LogObject<pointsExpirationSettingsParams>} LogObject<pointsExpirationSettingsParams>
 */
export function pointsExpirationSettings(params) {
    return { evid: 140, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyPointsHistoryClick' event (evid: 141)
 *
 * Event description: User clicks on a link in the Loyalty tab in Contacts section
 *
 * @param {loyaltyPointsHistoryClickParams} params - The parameters for this event
 * @property {string} activity_type - Activity type being shown in history
 * @return {LogObject<loyaltyPointsHistoryClickParams>} LogObject<loyaltyPointsHistoryClickParams>
 */
export function loyaltyPointsHistoryClick(params) {
    return { evid: 141, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyPointsBulkGiveOrEdit' event (evid: 142)
 *
 * Event description: when User gives or edits UoU points in bulk
 *
 * @param {loyaltyPointsBulkGiveOrEditParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} origin - dashboard/editor
 * @property {string} flow - there are two flows: edit (to edit/adjust points) & give (to give points)
 * @property {number} contactsSelected - amount of uuids that user selected
 * @property {number} amountPoints - how many points did the user adjust to/gives
 * @return {LogObject<loyaltyPointsBulkGiveOrEditParams>} LogObject<loyaltyPointsBulkGiveOrEditParams>
 */
export function loyaltyPointsBulkGiveOrEdit(params) {
    return { evid: 142, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyPointsError' event (evid: 143)
 *
 * Event description: What error does the user get when adjusting/giving points
 *
 * @param {loyaltyPointsErrorParams} params - The parameters for this event
 * @property {string} missingInfo - action/points
 * @return {LogObject<loyaltyPointsErrorParams>} LogObject<loyaltyPointsErrorParams>
 */
export function loyaltyPointsError(params) {
    return { evid: 143, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyRewardCreatedByAiAssistant' event (evid: 3)
 *
 * Event description: Loyalty reward creation from the loyalty AI assistant
 *
 * @param {loyaltyRewardCreatedByAiAssistantParams} params - The parameters for this event
 * @property {string} pointsRequiredMap - map of tier_ids and points required
 * @property {GUID} rewardId - reward_id from backend
 * @property {string} rewardType - type of reward
 * @property {string} rewardName - displayed reward name
 * @property {number} toGet - amount that customer will get, null if the reward is not monetary
 * @property {number} pointsRequired - points required to use this reward
 * @property {string} currency - currency in which the reward is given, null if not applicable
 * @return {LogObject<loyaltyRewardCreatedByAiAssistantParams>} LogObject<loyaltyRewardCreatedByAiAssistantParams>
 */
export function loyaltyRewardCreatedByAiAssistant(params) {
    return { evid: 3, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyEditorSettingsOpen' event (evid: 300)
 *
 * Event description: Sent when settings are loaded at editor
 *
 * @param {loyaltyEditorSettingsOpenParams} params - The parameters for this event
 * @return {LogObject<loyaltyEditorSettingsOpenParams>} LogObject<loyaltyEditorSettingsOpenParams>
 */
export function loyaltyEditorSettingsOpen(params) {
    return { evid: 300, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyEditorSettingsChangeTab' event (evid: 301)
 *
 * Event description: User change tab at settings
 *
 * @param {loyaltyEditorSettingsChangeTabParams} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} tabName - tab name user clicked to
 * @return {LogObject<loyaltyEditorSettingsChangeTabParams>} LogObject<loyaltyEditorSettingsChangeTabParams>
 */
export function loyaltyEditorSettingsChangeTab(params) {
    return { evid: 301, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyEditorSettingsDisplayTab' event (evid: 302)
 *
 * Event description: display tab changes
 *
 * @param {loyaltyEditorSettingsDisplayTabParams} params - The parameters for this event
 * @property {string} fieldName - field name
 * @property {boolean} status - T-checked/F-unchecked
 * @return {LogObject<loyaltyEditorSettingsDisplayTabParams>} LogObject<loyaltyEditorSettingsDisplayTabParams>
 */
export function loyaltyEditorSettingsDisplayTab(params) {
    return { evid: 302, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUserClickEditor' event (evid: 303)
 *
 * Event description: Loyalty User Click - Editor.
 *
 * @param {loyaltyUserClickEditorParams} params - The parameters for this event
 * @property {string} value - name of the button, link, toggle (etc) that was clicked by user
 * @return {LogObject<loyaltyUserClickEditorParams>} LogObject<loyaltyUserClickEditorParams>
 */
export function loyaltyUserClickEditor(params) {
    return { evid: 303, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyEditorSettingsTextTab' event (evid: 304)
 *
 * Event description: text tab changes
 *
 * @param {loyaltyEditorSettingsTextTabParams} params - The parameters for this event
 * @property {string} value - value that was entered
 * @property {string} fieldName - field name
 * @return {LogObject<loyaltyEditorSettingsTextTabParams>} LogObject<loyaltyEditorSettingsTextTabParams>
 */
export function loyaltyEditorSettingsTextTab(params) {
    return { evid: 304, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyMyRewardsEditorSettingsOpen' event (evid: 305)
 *
 * Event description: Sent when My Rewards settings are loaded at editor
 *
 * @param {loyaltyMyRewardsEditorSettingsOpenParams} params - The parameters for this event
 * @return {LogObject<loyaltyMyRewardsEditorSettingsOpenParams>} LogObject<loyaltyMyRewardsEditorSettingsOpenParams>
 */
export function loyaltyMyRewardsEditorSettingsOpen(params) {
    return { evid: 305, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyMrEditorSettingsChangeTab' event (evid: 306)
 *
 * Event description: User change tab at My Rewards settings
 *
 * @param {loyaltyMrEditorSettingsChangeTabParams} params - The parameters for this event
 * @property {string} tabName - tab name user clicked to
 * @property {string} referralInfo - place where user came from
 * @return {LogObject<loyaltyMrEditorSettingsChangeTabParams>} LogObject<loyaltyMrEditorSettingsChangeTabParams>
 */
export function loyaltyMrEditorSettingsChangeTab(params) {
    return { evid: 306, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyMrEditorSettingsTextTab' event (evid: 307)
 *
 * Event description: text tab changes
 *
 * @param {loyaltyMrEditorSettingsTextTabParams} params - The parameters for this event
 * @property {string} value - value that was entered
 * @property {string} fieldName - field_name
 * @return {LogObject<loyaltyMrEditorSettingsTextTabParams>} LogObject<loyaltyMrEditorSettingsTextTabParams>
 */
export function loyaltyMrEditorSettingsTextTab(params) {
    return { evid: 307, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyWidgetEditorSettingsOpenSrc136Evid330' event (evid: 330)
 *
 * Event description: Sent when Loyalty Widget settings are loaded at editor
 *
 * @param {loyaltyWidgetEditorSettingsOpenSrc136Evid330Params} params - The parameters for this event
 * @property {string} widget_id - widget unique GUID
 * @return {LogObject<loyaltyWidgetEditorSettingsOpenSrc136Evid330Params>} LogObject<loyaltyWidgetEditorSettingsOpenSrc136Evid330Params>
 */
export function loyaltyWidgetEditorSettingsOpenSrc136Evid330(params) {
    return { evid: 330, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyWidgetEditorSettingsChangeTabSrc136Evid331' event (evid: 331)
 *
 * Event description: User change tab at Loyalty Widget settings
 *
 * @param {loyaltyWidgetEditorSettingsChangeTabSrc136Evid331Params} params - The parameters for this event
 * @property {string} referralInfo - place where user came from
 * @property {string} tabName - tab name user clicked to
 * @property {GUID} widget_id - widget unique GUID
 * @return {LogObject<loyaltyWidgetEditorSettingsChangeTabSrc136Evid331Params>} LogObject<loyaltyWidgetEditorSettingsChangeTabSrc136Evid331Params>
 */
export function loyaltyWidgetEditorSettingsChangeTabSrc136Evid331(params) {
    return { evid: 331, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyWidgetEditorSettingsChangeSrc136Evid332' event (evid: 332)
 *
 * Event description: Loyalty Widget settings change
 *
 * @param {loyaltyWidgetEditorSettingsChangeSrc136Evid332Params} params - The parameters for this event
 * @property {string} fieldName - field name (parameter)
 * @property {boolean} status - T-checked/F-unchecked if it is toggle or checkbutton
 * @property {string} tabName - tab name
 * @property {string} value - json format information about changes (e.g. {"style":{"bold":true,"italic":false,"underline":false},"size":25,"preset":"Heading-S","family":"fraunces_120pt-light","fontStyleParam":true} )
 * @property {GUID} widget_id - widget id
 * @return {LogObject<loyaltyWidgetEditorSettingsChangeSrc136Evid332Params>} LogObject<loyaltyWidgetEditorSettingsChangeSrc136Evid332Params>
 */
export function loyaltyWidgetEditorSettingsChangeSrc136Evid332(params) {
    return { evid: 332, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyWidgetModalInEditorActionSrc136Evid333' event (evid: 333)
 *
 * Event description: Fires when Loyalty Widget "Add a Loyalty widget to promote your program" modal is: 1) shown 2) cancel 3) "Add Widget" clicked
 *
 * @param {loyaltyWidgetModalInEditorActionSrc136Evid333Params} params - The parameters for this event
 * @property {string} action - modal_opened / modal_canceled / add_widget
 * @property {string} open_origin - first_time_shown / add_ons
 * @return {LogObject<loyaltyWidgetModalInEditorActionSrc136Evid333Params>} LogObject<loyaltyWidgetModalInEditorActionSrc136Evid333Params>
 */
export function loyaltyWidgetModalInEditorActionSrc136Evid333(params) {
    return { evid: 333, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyAiAssistantConversationOpen' event (evid: 4)
 *
 * Event description: Sent when the user clicks on any button that initiates conversation with the AI Loyalty assistant
 *
 * @param {loyaltyAiAssistantConversationOpenParams} params - The parameters for this event
 * @property {string} origin - where the click came from  - modal, dropdown, no rewards CTA
 * @return {LogObject<loyaltyAiAssistantConversationOpenParams>} LogObject<loyaltyAiAssistantConversationOpenParams>
 */
export function loyaltyAiAssistantConversationOpen(params) {
    return { evid: 4, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyRewardDropdownClick' event (evid: 5)
 *
 * Event description: Sent when user clicks in the dropdown 'Create Reward'. There are currently 3 options - create reward, create with AI, create with functions. Also implemented on Mobile
 *
 * @param {loyaltyRewardDropdownClickParams} params - The parameters for this event
 * @property {string} createRewardClicked - create reward, create with AI, create with functions
 * @return {LogObject<loyaltyRewardDropdownClickParams>} LogObject<loyaltyRewardDropdownClickParams>
 */
export function loyaltyRewardDropdownClick(params) {
    return { evid: 5, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyJoinNowOrViewPoints' event (evid: 500)
 *
 * Event description: Event should be triggered when UoU clicks Join Now or View Points
 *
 * @param {loyaltyJoinNowOrViewPointsParams} params - The parameters for this event
 * @property {string} role - role of user that clicked button: owner, member, visitor
 * @property {string} button - button name user clicked: join_now, view_points, points_widget
 * @property {string} widget_id - GUID if `points_widget` is clicked
 * @return {LogObject<loyaltyJoinNowOrViewPointsParams>} LogObject<loyaltyJoinNowOrViewPointsParams>
 */
export function loyaltyJoinNowOrViewPoints(params) {
    return { evid: 500, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyProgramOpen' event (evid: 501)
 *
 * Event description: when Loyalty program page opens. If empty state then response from API will be in response field
 *
 * @param {loyaltyProgramOpenParams} params - The parameters for this event
 * @property {string} role - role of user that clicked button: owner, member, visitor
 * @property {string} response - API response: UNKNOWN, DRAFT, PAUSED
 * @return {LogObject<loyaltyProgramOpenParams>} LogObject<loyaltyProgramOpenParams>
 */
export function loyaltyProgramOpen(params) {
    return { evid: 501, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUouClick' event (evid: 502)
 *
 * Event description: triggered when UoU click
 *
 * @param {loyaltyUouClickParams} params - The parameters for this event
 * @property {string} value - name of the button, link, toggle (etc) that was clicked by user
 * @property {string} origin - button origin (my_rewards_page, popup, etc)
 * @return {LogObject<loyaltyUouClickParams>} LogObject<loyaltyUouClickParams>
 */
export function loyaltyUouClick(params) {
    return { evid: 502, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyMyRewardsOpen' event (evid: 503)
 *
 * Event description: When My Rewards tab opens
 *
 * @param {loyaltyMyRewardsOpenParams} params - The parameters for this event
 * @property {string} referralInfo - where click came from
 * @property {string} availableRewards - list of reward_id (guids) that are available
 * @property {string} unavailableRewards - list of reward_id (guids) that are unavailable
 * @property {number} totalRewards - number of total rewards user can see
 * @property {number} totalPoints - number of points user has
 * @return {LogObject<loyaltyMyRewardsOpenParams>} LogObject<loyaltyMyRewardsOpenParams>
 */
export function loyaltyMyRewardsOpen(params) {
    return { evid: 503, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUouApplyRewardClick' event (evid: 504)
 *
 * Event description: UoU clicks Apply Reward or redeems a coupon
 *
 * @param {loyaltyUouApplyRewardClickParams} params - The parameters for this event
 * @property {string} referralInfo - where click came from
 * @property {string} availableRewards - list of reward_id (guids) that are available
 * @property {string} unavailableRewards - list of reward_id (guids) that are unavailable
 * @property {GUID} rewardId - reward id
 * @property {string} rewardType - reward type
 * @property {number} totalRewards - number of total rewards user can see
 * @property {number} totalPoints - number of points user has
 * @property {string} rewardName - reward name that was used
 * @return {LogObject<loyaltyUouApplyRewardClickParams>} LogObject<loyaltyUouApplyRewardClickParams>
 */
export function loyaltyUouApplyRewardClick(params) {
    return { evid: 504, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyUouBuyCoupon' event (evid: 506)
 *
 * Event description: UoU clicks for buying coupons/rewards
 *
 * @param {loyaltyUouBuyCouponParams} params - The parameters for this event
 * @property {string} referralInfo - where click came from - send checkout
 * @property {string} availableRewards - list of reward_id (guids) that are available
 * @property {string} unavailableRewards - list of reward_id (guids) that are unavailable
 * @property {number} rewardPrice - reward price in points
 * @property {GUID} rewardId - reward id
 * @property {string} rewardType - reward type
 * @property {number} totalRewards - number of total rewards user can see
 * @property {number} totalPoints - number of points user has before buying
 * @property {string} rewardName - reward name that was used
 * @return {LogObject<loyaltyUouBuyCouponParams>} LogObject<loyaltyUouBuyCouponParams>
 */
export function loyaltyUouBuyCoupon(params) {
    return { evid: 506, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyProgramStatusChange' event (evid: 800)
 *
 * Event description: Server side BI event that is being triggered when Loyalty Program statuses changes. Draft, Active, Paused
 *
 * @param {loyaltyProgramStatusChangeParams} params - The parameters for this event
 * @property {string} pointsName - points name
 * @property {string} programName - program name
 * @property {number} revision - revision
 * @property {string} programStatus - Draft, Active, Paused
 * @return {LogObject<loyaltyProgramStatusChangeParams>} LogObject<loyaltyProgramStatusChangeParams>
 */
export function loyaltyProgramStatusChange(params) {
    return { evid: 800, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyAllRewardsAndRewardsDisabled' event (evid: 801)
 *
 * Event description: sent when User disables all rules and rewards
 *
 * @param {loyaltyAllRewardsAndRewardsDisabledParams} params - The parameters for this event
 * @property {GUID} msid - msid
 * @return {LogObject<loyaltyAllRewardsAndRewardsDisabledParams>} LogObject<loyaltyAllRewardsAndRewardsDisabledParams>
 */
export function loyaltyAllRewardsAndRewardsDisabled(params) {
    return { evid: 801, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loyaltyTransactionSuccessT' event (evid: 901)
 *
 * Event description: sent on successful loyalty transaction (earn, adjust, redeem)
 *
 * @param {loyaltyTransactionSuccessTParams} params - The parameters for this event
 * @property {string} sourceAppId - originating app id (mostly used for adjust transactions)
 * @property {GUID} referenceEntityId - reference entity id
 * @property {string} transactionType - earn/redeem/adjust
 * @property {number} amount - amount of loyalty points earned/redeemed/issued
 * @property {GUID} rewardId - reward id
 * @property {string} rewardType - reward type e.g. DISCOUNT_AMOUNT, COUPON, etc
 * @property {GUID} contactId - contact_id of the member
 * @property {GUID} memberId - member id
 * @property {GUID} loyaltyAccountId - loyalty account id
 * @property {GUID} transactionId - transaction id
 * @property {GUID} refundedTransactionId - in case of the refund, the transaction_id of the original redeem action
 * @return {LogObject<loyaltyTransactionSuccessTParams>} LogObject<loyaltyTransactionSuccessTParams>
 */
export function loyaltyTransactionSuccessT(params) {
    return { evid: 901, src: 136, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsOpenBo' event (evid: 101)
 *
 * Event description: Fired when BO opens
 *
 * @param {referralsOpenBoParams} params - The parameters for this event
 * @property {string} origin - editor/dashboard
 * @return {LogObject<referralsOpenBoParams>} LogObject<referralsOpenBoParams>
 */
export function referralsOpenBo(params) {
    return { evid: 101, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsStartClick' event (evid: 102)
 *
 * Event description: User clicks Start in Program creation flow
 *
 * @param {referralsStartClickParams} params - The parameters for this event
 * @property {string} value - CTA name
 * @return {LogObject<referralsStartClickParams>} LogObject<referralsStartClickParams>
 */
export function referralsStartClick(params) {
    return { evid: 102, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsMiscClick' event (evid: 103)
 *
 * Event description: User clicks one of non-essential program setup buttons
 *
 * @param {referralsMiscClickParams} params - The parameters for this event
 * @property {string} value - button name (upgrade, learn more, etc.)
 * @return {LogObject<referralsMiscClickParams>} LogObject<referralsMiscClickParams>
 */
export function referralsMiscClick(params) {
    return { evid: 103, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsManageProgramClick' event (evid: 104)
 *
 * Event description: User clicks Manage program in BM
 *
 * @param {referralsManageProgramClickParams} params - The parameters for this event
 * @return {LogObject<referralsManageProgramClickParams>} LogObject<referralsManageProgramClickParams>
 */
export function referralsManageProgramClick(params) {
    return { evid: 104, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsOpenManageProgram' event (evid: 105)
 *
 * Event description: User opens Manage program dialog
 *
 * @param {referralsOpenManageProgramParams} params - The parameters for this event
 * @property {string} origin - editor/dashboard
 * @return {LogObject<referralsOpenManageProgramParams>} LogObject<referralsOpenManageProgramParams>
 */
export function referralsOpenManageProgram(params) {
    return { evid: 105, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsManageProgramEmailsAction' event (evid: 106)
 *
 * Event description: User enables/disables email automations
 *
 * @param {referralsManageProgramEmailsActionParams} params - The parameters for this event
 * @property {string} ruleType - type of email
 * @property {string} action - type of action (on-off/edit)
 * @return {LogObject<referralsManageProgramEmailsActionParams>} LogObject<referralsManageProgramEmailsActionParams>
 */
export function referralsManageProgramEmailsAction(params) {
    return { evid: 106, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsProgramSaveClick' event (evid: 107)
 *
 * Event description: User saves program from Manage program form
 *
 * @param {referralsProgramSaveClickParams} params - The parameters for this event
 * @return {LogObject<referralsProgramSaveClickParams>} LogObject<referralsProgramSaveClickParams>
 */
export function referralsProgramSaveClick(params) {
    return { evid: 107, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsDiscardChangesModalAction' event (evid: 108)
 *
 * Event description: Actions of Manage program changes discard modal
 *
 * @param {referralsDiscardChangesModalActionParams} params - The parameters for this event
 * @property {string} modalType - discard/leave/fields missing
 * @property {string} action - open/confirm
 * @return {LogObject<referralsDiscardChangesModalActionParams>} LogObject<referralsDiscardChangesModalActionParams>
 */
export function referralsDiscardChangesModalAction(params) {
    return { evid: 108, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsAiSocialPromotionEditClickSrc163Evid201' event (evid: 201)
 *
 * Event description: Sent when user clicks 'Edit' on a specific social media generated post
 *
 * @param {referralsAiSocialPromotionEditClickSrc163Evid201Params} params - The parameters for this event
 * @property {string} postTopic - the topic on which the post was generated
 * @property {string} postText - the text of the generated post
 * @return {LogObject<referralsAiSocialPromotionEditClickSrc163Evid201Params>} LogObject<referralsAiSocialPromotionEditClickSrc163Evid201Params>
 */
export function referralsAiSocialPromotionEditClickSrc163Evid201(params) {
    return { evid: 201, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsAiSocialPromotionRegenerateSrc163Evid202' event (evid: 202)
 *
 * Event description: User clicks to regenerate AI generated posts
 *
 * @param {referralsAiSocialPromotionRegenerateSrc163Evid202Params} params - The parameters for this event
 * @property {string} postTopic - topic which user chose the regenerate
 * @return {LogObject<referralsAiSocialPromotionRegenerateSrc163Evid202Params>} LogObject<referralsAiSocialPromotionRegenerateSrc163Evid202Params>
 */
export function referralsAiSocialPromotionRegenerateSrc163Evid202(params) {
    return { evid: 202, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsAiSocialPromotionCopyLinkSrc163Evid203' event (evid: 203)
 *
 * Event description: User clicks to copy link to their referral program
 *
 * @param {referralsAiSocialPromotionCopyLinkSrc163Evid203Params} params - The parameters for this event
 * @return {LogObject<referralsAiSocialPromotionCopyLinkSrc163Evid203Params>} LogObject<referralsAiSocialPromotionCopyLinkSrc163Evid203Params>
 */
export function referralsAiSocialPromotionCopyLinkSrc163Evid203(params) {
    return { evid: 203, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsAiSocialPromotionTopicChangeSrc163Evid204' event (evid: 204)
 *
 * Event description: Sent when user changes the post topic. In the case of 'Other', does not provide the user text input
 *
 * @param {referralsAiSocialPromotionTopicChangeSrc163Evid204Params} params - The parameters for this event
 * @property {string} previousTopic - what topic the user was previously on
 * @property {string} clickedTopic - what topic the user clicked on
 * @return {LogObject<referralsAiSocialPromotionTopicChangeSrc163Evid204Params>} LogObject<referralsAiSocialPromotionTopicChangeSrc163Evid204Params>
 */
export function referralsAiSocialPromotionTopicChangeSrc163Evid204(params) {
    return { evid: 204, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsAiSocialPromotionErrorClickSrc163Evid205' event (evid: 205)
 *
 * Event description: Sent when users clicks in a post and does not have the requirements to use it
 *
 * @param {referralsAiSocialPromotionErrorClickSrc163Evid205Params} params - The parameters for this event
 * @property {string} errorReason - The reason why the user cannot edit the post - no pages, no premium, site not published
 * @return {LogObject<referralsAiSocialPromotionErrorClickSrc163Evid205Params>} LogObject<referralsAiSocialPromotionErrorClickSrc163Evid205Params>
 */
export function referralsAiSocialPromotionErrorClickSrc163Evid205(params) {
    return { evid: 205, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsReferrerClick' event (evid: 301)
 *
 * Event description: Fires on actions done by referring users
 *
 * @param {referralsReferrerClickParams} params - The parameters for this event
 * @property {string} value - Login to refer, Copy link, Link Text Input Click, Share, Refer (mobile)
 * @return {LogObject<referralsReferrerClickParams>} LogObject<referralsReferrerClickParams>
 */
export function referralsReferrerClick(params) {
    return { evid: 301, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsReferredFriendAction' event (evid: 302)
 *
 * Event description: Fires on actions by referred friend
 *
 * @param {referralsReferredFriendActionParams} params - The parameters for this event
 * @property {string} value - Get Reward, Copy code, Coupon Text Input Click, Signup, Go to home
 * @return {LogObject<referralsReferredFriendActionParams>} LogObject<referralsReferredFriendActionParams>
 */
export function referralsReferredFriendAction(params) {
    return { evid: 302, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralsCouponGeneratedAndLoaded' event (evid: 303)
 *
 * Event description: Sent when coupon is generated and displayed for a referred friend
 *
 * @param {referralsCouponGeneratedAndLoadedParams} params - The parameters for this event
 * @property {number} loadTime - Time in seconds it took to generate and load the Coupon
 * @return {LogObject<referralsCouponGeneratedAndLoadedParams>} LogObject<referralsCouponGeneratedAndLoadedParams>
 */
export function referralsCouponGeneratedAndLoaded(params) {
    return { evid: 303, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralProgramOaLoad' event (evid: 500)
 *
 * Event description: User loads a Referral program in owner app
 *
 * @param {referralProgramOaLoadParams} params - The parameters for this event
 * @property {string} programStatus - Referral program status (Draft/Paused/Active)
 * @return {LogObject<referralProgramOaLoadParams>} LogObject<referralProgramOaLoadParams>
 */
export function referralProgramOaLoad(params) {
    return { evid: 500, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralProgramOaManageProgram' event (evid: 501)
 *
 * Event description: User edits/saves a Referral program in owner app
 *
 * @param {referralProgramOaManageProgramParams} params - The parameters for this event
 * @property {string} flowState - raw value of the flow - current_state / ticket_ordered / etc. (only sent when action=save)
 * @property {string} flow - reward_trigger / referring_customer_reward / referred_friend_reward / emails
 * @property {string} action - edit / save
 * @return {LogObject<referralProgramOaManageProgramParams>} LogObject<referralProgramOaManageProgramParams>
 */
export function referralProgramOaManageProgram(params) {
    return { evid: 501, src: 163, endpoint: '', params };
}
/**
 * A function to create a log object for the 'referralProgramOaUpgradeLearnMore' event (evid: 502)
 *
 * Event description: User clicks Learn more from the Upgrade banner in owner app
 *
 * @param {referralProgramOaUpgradeLearnMoreParams} params - The parameters for this event
 * @return {LogObject<referralProgramOaUpgradeLearnMoreParams>} LogObject<referralProgramOaUpgradeLearnMoreParams>
 */
export function referralProgramOaUpgradeLearnMore(params) {
    return { evid: 502, src: 163, endpoint: '', params };
}
//# sourceMappingURL=index.js.map